import { can } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { Download, ExternalLink, Plus } from 'lucide-react';
import { lazy, Suspense, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { projectQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { sitePhotosLink } from '../site/SitePhotosPage';
import {
  NewQuestionDialog,
  NewRemarkDialog,
  QuestionsList,
  RemarksList,
} from '../supervision/SupervisionPage';
import { SaveOffline } from './SaveOffline';
import { projectTarget, RevisionBadge } from './shared';

/** Просмотрщик PDF грузится отдельно: он тяжёлый и нужен только на странице листа */
const SheetViewer = lazy(() => import('./SheetViewer'));

interface SheetView {
  id: string;
  number: string;
  name: string;
  setId: string;
  setCode: string;
  setName: string;
  projectId: string;
  projectCode: string;
  activeOnly: boolean;
  versions: Array<{
    revisionId: string;
    revision: string;
    status: string;
    stampDate: string | null;
    fileId: string | null;
    fileName: string | null;
    page: number | null;
    changeNo: string | null;
    changed: boolean;
  }>;
}

/**
 * Лист: действующий файл в просмотре, история ревизий, вопросы и замечания на этом листе (ВП-05).
 * Прораб видит только действующую ревизию.
 */
export function SheetPage() {
  const { sheetId } = useParams({ strict: false }) as { sheetId: string };
  const me = useMe();
  const q = useQuery({
    queryKey: ['psd-sheet', sheetId],
    queryFn: () => api<SheetView>(`/psd/sheets/${sheetId}`),
  });
  const project = useQuery({ ...projectQuery(q.data?.projectId ?? ''), enabled: Boolean(q.data?.projectId) });
  const [pick, setPick] = useState<string | null>(null);
  const [newQ, setNewQ] = useState(false);
  const [newR, setNewR] = useState(false);
  const [point, setPoint] = useState<{ x: number; y: number } | null>(null);
  const [pointPick, setPointPick] = useState<{ x: number; y: number } | null>(null);
  useCrumbs(
    q.data
      ? [
          { label: q.data.projectCode, to: `/psd?project=${q.data.projectId}` },
          { label: q.data.setCode, to: `/psd/sets/${q.data.setId}` },
          { label: `Лист ${q.data.number}` },
        ]
      : null,
    q.error,
  );
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const s = q.data;
  const active = s.versions.find((v) => v.status === 'production');
  const shown = s.versions.find((v) => v.revisionId === pick) ?? active ?? s.versions[0];
  const target = project.data ? projectTarget(project.data) : null;
  const canAsk = target
    ? can(me.access, 'supervision.respond', target) || can(me.access, 'supervision.issue', target)
    : false;
  const canIssue = target ? can(me.access, 'supervision.issue', target) : false;
  // Фото площадки, привязанные к листу в отчёте смены (ПР-03): площадка открыта сотрудникам с правом на неё
  const canSitePhotos =
    Boolean(target) &&
    (me.tenant?.staff ?? false) &&
    ['site.view', 'site.report', 'site.review'].some((x) => can(me.access, x, target!));
  const fileUrl = shown?.fileId
    ? `/api/v1/psd/files/${shown.fileId}${shown.page ? `#page=${shown.page}` : ''}`
    : null;
  return (
    <Page>
      <PageHeader
        title={`Лист ${s.number}`}
        subtitle={`${s.name} · ${s.setCode}`}
        actions={
          <>
            {canAsk ? (
              <Button
                variant="primary"
                size="lg"
                icon={<Plus className="size-4" />}
                onClick={() => setNewQ(true)}
              >
                Вопрос по листу
              </Button>
            ) : null}
            {canIssue ? (
              <Button size="lg" icon={<Plus className="size-4" />} onClick={() => setNewR(true)}>
                Замечание по листу
              </Button>
            ) : null}
          </>
        }
      />
      <div className="mb-4">
        <SaveOffline
          label={`Лист ${s.number}`}
          activeRevisionId={active?.revisionId ?? null}
          sheets={
            active?.fileId
              ? [
                  {
                    sheetId: s.id,
                    number: s.number,
                    setId: s.setId,
                    setCode: s.setCode,
                    revisionId: active.revisionId,
                    revision: active.revision,
                    fileId: active.fileId,
                    fileName: active.fileName,
                  },
                ]
              : []
          }
        />
      </div>
      <div className="grid gap-4 xl:grid-cols-[1fr_400px]">
        <div className="flex min-w-0 flex-col gap-4">
          {shown ? (
            <Panel
              title={`Ревизия ${shown.revision}`}
              meta={<RevisionBadge status={shown.status} />}
              actions={
                shown.fileId ? (
                  <>
                    <a
                      href={fileUrl!}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-11 items-center gap-1.5 rounded-md border border-line-strong px-3 text-[14px] text-ink hover:underline md:h-10"
                    >
                      <ExternalLink className="size-4" /> Открыть
                    </a>
                    <a
                      href={`/api/v1/psd/files/${shown.fileId}?download=1`}
                      className="inline-flex h-11 items-center gap-1.5 rounded-md border border-line-strong px-3 text-[14px] text-ink hover:underline md:h-10"
                    >
                      <Download className="size-4" /> Скачать
                    </a>
                  </>
                ) : null
              }
              bodyClassName="p-0"
            >
              {shown.status === 'production' ? null : (
                <p className="border-b border-line bg-warning-soft px-5 py-2.5 text-[13px] text-warning">
                  Это не действующая ревизия: на площадке работают по{' '}
                  {active ? active.revision : 'ревизии со штампом, когда её выпустят'}.
                </p>
              )}
              {fileUrl && shown.fileName?.toLowerCase().endsWith('.pdf') ? (
                <Suspense fallback={<Spinner label="Открываем просмотр" />}>
                  <SheetViewer
                    fileUrl={`/api/v1/psd/files/${shown.fileId}`}
                    startPage={shown.page ?? 1}
                    sheetId={s.id}
                    revisionId={shown.revisionId}
                    canMarkup={shown.status !== 'annulled'}
                    canPoint={(canAsk || canIssue) && shown.status === 'production'}
                    canClose={target ? can(me.access, 'psd.approve', target) : false}
                    onPoint={(p) => {
                      if (canAsk && canIssue) setPointPick(p);
                      else {
                        setPoint(p);
                        if (canIssue) setNewR(true);
                        else setNewQ(true);
                      }
                    }}
                  />
                </Suspense>
              ) : shown.fileId ? (
                <p className="px-5 py-6 text-[14px] text-ink-2">
                  Файл {shown.fileName} открывается кнопкой «Открыть». Просмотр DWG и IFC в браузере —
                  следующим этапом.
                </p>
              ) : (
                <p className="px-5 py-6 text-[14px] text-ink-2">Файл листа ещё не загружен.</p>
              )}
            </Panel>
          ) : (
            <EmptyState title="Действующей ревизии нет">
              Лист появится, когда ГИП поставит штамп «В производство работ».
            </EmptyState>
          )}
          {s.versions.length > 1 ? (
            <Panel
              title="Ревизии листа"
              bodyClassName="p-0"
              actions={
                s.versions.filter((v) => v.fileName?.toLowerCase().endsWith('.pdf')).length > 1 ? (
                  <Link
                    to="/psd/sheets/$sheetId/compare"
                    params={{ sheetId: s.id }}
                    search={{} as never}
                    className="inline-flex h-11 items-center rounded-md border border-line-strong px-3 text-[14px] text-ink hover:underline md:h-9"
                  >
                    Сравнить ревизии
                  </Link>
                ) : null
              }
            >
              <ul className="divide-y divide-line">
                {s.versions.map((v) => (
                  <li key={v.revisionId}>
                    <button
                      type="button"
                      onClick={() => setPick(v.revisionId)}
                      className={clsx(
                        'flex min-h-11 w-full flex-wrap items-center gap-2 px-5 py-2.5 text-left text-[14px] hover:bg-surface-2',
                        v.revisionId === shown?.revisionId && 'bg-surface-2 font-medium',
                      )}
                    >
                      <span className="num w-12">{v.revision}</span>
                      <RevisionBadge status={v.status} />
                      {v.changed ? (
                        <span className="text-[13px] text-ink-2">лист изменён</span>
                      ) : (
                        <span className="text-[13px] text-ink-3">без изменений</span>
                      )}
                      {v.stampDate ? (
                        <span className="text-[13px] text-ink-2">штамп {date(v.stampDate)}</span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}
        </div>
        <div className="flex flex-col gap-4">
          <Panel title="Вопросы к проекту по листу">
            <QuestionsList compact search={{ sheet: s.id, status: 'all' }} />
          </Panel>
          <Panel title="Замечания по листу">
            <RemarksList compact search={{ sheet: s.id, status: 'all' }} />
          </Panel>
          <Link
            to="/psd/sets/$setId"
            params={{ setId: s.setId }}
            className="inline-flex min-h-11 items-center text-[14px] text-ink underline-offset-4 hover:underline"
          >
            Весь комплект {s.setCode}
          </Link>
          {canSitePhotos ? (
            <Link
              {...sitePhotosLink(s.projectId, { sheet: s.id })}
              className="inline-flex min-h-11 items-center text-[14px] text-ink underline-offset-4 hover:underline"
            >
              Фото площадки по листу
            </Link>
          ) : null}
        </div>
      </div>
      {newQ ? (
        <NewQuestionDialog
          projectId={s.projectId}
          setId={s.setId}
          sheetId={s.id}
          point={point ?? undefined}
          onClose={() => {
            setNewQ(false);
            setPoint(null);
          }}
        />
      ) : null}
      {newR ? (
        <NewRemarkDialog
          projectId={s.projectId}
          setId={s.setId}
          sheetId={s.id}
          point={point ?? undefined}
          onClose={() => {
            setNewR(false);
            setPoint(null);
          }}
        />
      ) : null}
      {pointPick ? (
        <Dialog
          open
          width="sm"
          onOpenChange={(o) => !o && setPointPick(null)}
          title="Что поставить в эту точку?"
        >
          <div className="flex flex-col gap-2">
            <Button
              variant="primary"
              size="lg"
              onClick={() => {
                setPoint(pointPick);
                setPointPick(null);
                setNewQ(true);
              }}
            >
              Вопрос к проекту
            </Button>
            <Button
              size="lg"
              onClick={() => {
                setPoint(pointPick);
                setPointPick(null);
                setNewR(true);
              }}
            >
              Замечание
            </Button>
          </div>
        </Dialog>
      ) : null}
    </Page>
  );
}
